import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  TrendingUp, 
  Shield, 
  Star, 
  Award, 
  Zap, 
  Target, 
  Brain,
  BarChart3,
  Phone,
  FileText,
  X
} from 'lucide-react';

import { useScorePageData } from '../hooks/useScorePageData';
import { useScoreData } from '../hooks/useScoreData';
import { getValidatedToken } from '../utils/tokenValidation';
import { tracker } from '../lib/tracker';
import { SeoAuditRequestService } from '../services/seoAuditRequestService';
import { CompanyLogo } from '../components/CompanyLogo';

// Composant d'indicateur de progression de lecture - Style TestPage2
const ReadingProgressIndicator: React.FC = () => {
  const [progress, setProgress] = useState(0);

  React.useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
      <div 
        className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const ScorePage2: React.FC = () => {
  const { token: pathToken } = useParams<{ token?: string }>();
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Token validation
  const effectiveToken = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return getValidatedToken(urlParams, pathToken);
  }, [pathToken]);

  // Data hooks
  const {
    overallScore,
    siteUrl,
    prospectEmail,
    prospectId,
    quizCompleted,
    performanceMetrics,
    loading,
    error,
    refreshData
  } = useScorePageData(effectiveToken || undefined);

  const score = useScoreData(overallScore, performanceMetrics, siteUrl);

  // Fonction pour enregistrer la demande d'audit (avec email du prospect via token)
  const handleRequestAudit = async () => {
    try {
      console.log('🔍 [handleRequestAudit] Début - prospectEmail:', prospectEmail, 'prospectId:', prospectId, 'siteUrl:', siteUrl);

      // Récupérer l'email du prospect directement depuis les données chargées
      if (!prospectEmail) {
        console.error('❌ [handleRequestAudit] Email du prospect non trouvé');
        alert('Impossible de récupérer votre email. Veuillez réessayer.');
        return;
      }

      console.log('✅ [handleRequestAudit] Email trouvé, création de la demande...');

      // Enregistrer la demande dans la base de données
      const auditRequest = await SeoAuditRequestService.createRequest({
        email: prospectEmail,
        site_url: siteUrl || '',
        prospect_id: prospectId
      });

      console.log('✅ [handleRequestAudit] Demande créée avec succès:', auditRequest);

      // Envoyer l'email d'audit (fonction existante)
      const response = await fetch('https://ghfdrlkidgwnyjufpxfl.supabase.co/functions/v1/send-audit-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdoZmRybGtpZGd3bnlqdWZweGZsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2NjA2OTAsImV4cCI6MjA2MzIzNjY5MH0.DxiYOlmQ3tL5HlHfjqFPZFmmd7CyblXcfA60VyDbfEk',
        },
        body: JSON.stringify({
          token: effectiveToken,
          url: siteUrl,
          company: '',
          website: siteUrl,
          email: prospectEmail
        })
      });

      const result = await response.json();

      // Afficher toujours le popup de confirmation (même si l'envoi email échoue)
      setShowEmailPopup(true);

      if (!result.success) {
        console.error('Erreur envoi email:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la demande:', error);
      // Afficher quand même le popup de confirmation
      setShowEmailPopup(true);
    }
  };

  // Animation du score
  useEffect(() => {
    if (score.overall > 0) {
      const timer = setTimeout(() => {
        const interval = setInterval(() => {
          setAnimatedScore(prev => {
            if (prev >= score.overall) {
              clearInterval(interval);
              return score.overall;
            }
            return prev + 1;
          });
        }, 30);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [score.overall]);

  // Track page view
  useEffect(() => {
    if (effectiveToken) {
      tracker.trackScoreView(effectiveToken).catch(console.error);
    }
  }, [effectiveToken]);

  // Sticky CTA bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroHeight = 800; // Approximate hero section height
      setShowStickyBar(scrollPosition > heroHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonials carousel
  const testimonials = [
    {
      text: "Résultats exceptionnels ! +180% de trafic en 3 mois.",
      author: "Sarah M.",
      company: "E-commerce Suisse"
    },
    {
      text: "ROI incroyable. Notre CA a doublé en 6 mois.",
      author: "Marc L.",
      company: "Services B2B"
    },
    {
      text: "Enfin une agence qui tient ses promesses. +247% de leads.",
      author: "Julie R.",
      company: "Tech Startup"
    },
    {
      text: "Analyse précise et plan d'action clair. Résultats en 3 semaines.",
      author: "Thomas B.",
      company: "Retail Genève"
    },
    {
      text: "Meilleur investissement digital de l'année. +320% de visibilité.",
      author: "Céline D.",
      company: "PME Vaud"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Business impact calculation
  const businessImpact = useMemo(() => {
    const baseMonthlyLoss = Math.max(1000, (100 - score.overall) * 150);
    return {
      monthlyLoss: Math.round(baseMonthlyLoss),
      yearlyLoss: Math.round(baseMonthlyLoss * 12)
    };
  }, [score.overall]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Analyse en cours...</h2>
          <p className="text-gray-600">Calcul de votre score digital</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Erreur de chargement</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={refreshData}
              className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (effectiveToken === null) {
    return (
      <div className="min-h-screen bg-white">
        <main className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-md mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">Token invalide</h2>
              <p className="text-yellow-600">Le lien d'accès à votre audit semble invalide ou expiré.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Indicateur de progression de lecture */}
      <ReadingProgressIndicator />
      
      {/* Header optimisé mobile - Style TestPage2 */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40 backdrop-blur-sm bg-white/95">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-center">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 space-y-1 sm:space-y-0">
              <div className="flex-shrink-0">
                <img src="/images/smart_impact_logo.jpg" alt="Smart Impact" className="h-10 object-contain" />
              </div>
              <div className="text-center">
                <p className="text-gray-600 text-xs font-medium">
                  Agence digitale • Référencement • IA
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section - Style TestPage2 */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 md:py-20 lg:py-24 overflow-hidden">
        {/* Background Elements - Style TestPage2 */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 md:w-96 md:h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 md:w-80 md:h-80 bg-orange-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Badge - Style TestPage2 */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Audit Digital Complet
            </div>

            {/* Badge conditionnel quiz */}
            {!quizCompleted && (
              <div className="mb-4 inline-flex items-center px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-full text-yellow-800 text-sm font-medium">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Score partiel - Complétez le quiz pour le score final enrichi
              </div>
            )}

            {/* Titre - Style TestPage2 */}
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 leading-tight text-white">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                {quizCompleted ? "Votre SmartIndex™" : "Votre Score Digital"}
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl font-normal text-orange-400 mt-2">
                {animatedScore}%
              </span>
            </h1>
            
            <p className="mx-auto mb-8 max-w-3xl text-lg md:text-xl text-gray-300 leading-relaxed text-center">
              Découvrez l'impact de votre présence digitale sur votre chiffre d'affaires
            </p>

            {/* Site URL - Style TestPage2 */}
            <div className="mb-8">
              <div className="inline-block bg-white/10 rounded-xl px-6 py-3 backdrop-blur-sm border border-white/20">
                <span className="font-mono text-xl md:text-2xl font-bold text-orange-400 select-none pointer-events-none">
                  {siteUrl || 'votre-site.com'}
                </span>
              </div>
            </div>

            {/* Impact Business Magnétique */}
            <motion.div 
              className="bg-gradient-to-r from-red-500/20 to-orange-500/20 backdrop-blur-sm rounded-2xl p-8 mb-8 max-w-3xl mx-auto border border-red-400/30"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center px-4 py-2 bg-red-500/20 rounded-full text-red-300 text-sm font-medium mb-4">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Coût de l'Inaction
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Votre site à {animatedScore}% de performance vous fait perdre :
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-center bg-white/5 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-400 mb-1 whitespace-nowrap">Jusqu'à 68%</div>
                  <div className="text-sm text-gray-400">de trafic organique potentiel</div>
                  <div className="text-xs text-red-300 mt-1">Source : Ahrefs, Backlinko</div>
                </div>
                <div className="text-center bg-white/5 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-400 mb-1 whitespace-nowrap">2,6x moins</div>
                  <div className="text-sm text-gray-400">de conversions qu'un site optimisé</div>
                  <div className="text-xs text-red-300 mt-1">Source : HubSpot</div>
                </div>
                <div className="text-center bg-white/5 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-400 mb-1 whitespace-nowrap">+200%</div>
                  <div className="text-sm text-gray-400">de CPC sur vos campagnes Google Ads</div>
                  <div className="text-xs text-red-300 mt-1">Source : Wordstream</div>
                </div>
                <div className="text-center bg-white/5 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-400 mb-1 whitespace-nowrap">88%</div>
                  <div className="text-sm text-gray-400">des utilisateurs quittent après une mauvaise UX</div>
                  <div className="text-xs text-red-300 mt-1">Source : Forrester</div>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Avec optimisation</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-400/30">
                    <div className="text-3xl font-bold text-green-400 mb-1">+247%</div>
                    <div className="text-sm text-gray-400">de ROI en 6 mois</div>
                  </div>
                  <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-400/30">
                    <div className="text-3xl font-bold text-green-400 mb-1">+55%</div>
                    <div className="text-sm text-gray-400">de conversions</div>
                  </div>
                  <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-400/30">
                    <div className="text-3xl font-bold text-green-400 mb-1">+120%</div>
                    <div className="text-sm text-gray-400">de visibilité SEO</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CTAs Progressifs - Conversion Optimisée */}
            <div className="flex flex-col items-center gap-6">
              {/* CTA Primaire - Call avec Expert */}
              <motion.div className="text-center">
                <motion.a 
                  href="https://tidycal.com/smartimpact/25-minutes-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg md:text-xl px-10 md:px-16 py-5 md:py-7 rounded-2xl shadow-2xl hover:shadow-orange-500/40 transform hover:scale-105 transition-all duration-300 inline-flex items-center overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="flex items-center">
                      <Phone className="mr-3 group-hover:animate-pulse" size={24} />
                      <span>Débloquer +35% de croissance en 25min</span>
                    </div>
                    <span className="text-xs mt-1 opacity-90">Appel stratégique avec un expert • 100% gratuit</span>
                  </div>
                </motion.a>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-sm text-orange-200">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <strong>Prochaine dispo : Demain 14h</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    <strong>Sans engagement</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-current" />
                    <strong>4.9/5</strong>
                  </span>
                </div>
              </motion.div>
              
              {/* CTA Secondaire - Audit PDF */}
              <div className="text-center">
                <motion.button 
                  onClick={handleRequestAudit}
                  className="group flex items-center justify-center px-8 py-4 bg-white/10 rounded-xl text-white font-semibold hover:bg-white/20 transition-all duration-300 border border-white/30 hover:border-white/50"
                  whileHover={{ scale: 1.02 }}
                >
                  <FileText className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  Recevoir mon plan de croissance GRATUIT
                  <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">Valeur 790 CHF</span>
                </motion.button>
                <p className="text-xs text-orange-200 mt-2">Livraison sous 24h • Rapport PDF complet</p>
              </div>

              {/* Urgence Contrôlée avec compteur */}
              <div className="text-center">
                <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-400/30 rounded-xl p-4 max-w-md mx-auto">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                    <span className="text-red-300 text-sm font-bold">PLACES LIMITÉES</span>
                  </div>
                  <div className="text-white font-bold text-lg mb-2">
                    Seulement <span className="text-orange-400 text-2xl">2 places</span> restantes cette semaine
                  </div>
                  <div className="flex justify-center gap-1 mb-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-600 rounded-full"></div>
                  </div>
                  <p className="text-xs text-gray-400">Prochaine disponibilité : Semaine du 27 octobre</p>
                </div>
              </div>

              {/* Logos clients (preuve sociale) */}
              <div className="text-center mt-8">
                <p className="text-xs text-gray-400 mb-4">Ils nous font confiance</p>
                <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto px-4">
                  {/* Ligne 1 */}
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://swiss-instasolar.ch/wp-content/uploads/2024/06/Logo.svg"
                      alt="Swiss Instasolar"
                      className="max-h-8 md:max-h-10 w-auto opacity-60 hover:opacity-90 transition-opacity filter brightness-200 object-contain"
                    />
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://smart-impact.ch/wp-content/uploads/2025/03/helvi.png"
                      alt="Helvi"
                      className="max-h-8 md:max-h-10 w-auto opacity-60 hover:opacity-90 transition-opacity object-contain"
                    />
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://smart-impact.ch/wp-content/uploads/2025/03/swissbo_logo.png"
                      alt="Swissbo"
                      className="max-h-8 md:max-h-10 w-auto opacity-60 hover:opacity-90 transition-opacity filter brightness-200 object-contain"
                    />
                  </motion.div>

                  {/* Ligne 2 */}
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://smart-impact.ch/wp-content/uploads/2025/03/popepoppa.webp"
                      alt="Pope Poppa"
                      className="max-h-8 md:max-h-10 w-auto opacity-60 hover:opacity-90 transition-opacity object-contain"
                    />
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://www.mayortraiteur.ch/wp-content/uploads/2025/04/LOGO-MAYOR-TRAITEUR-pdf-1024x1022.jpg"
                      alt="Mayor Traiteur"
                      className="max-h-8 md:max-h-10 w-auto opacity-60 hover:opacity-90 transition-opacity object-contain"
                    />
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center h-12 md:h-14 bg-white/5 rounded-lg p-3 backdrop-blur-sm border border-white/10"
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src="https://asdfiduciaire.ch/wp-content/uploads/2025/10/White-Logo.svg"
                      alt="ASD Fiduciaire"
                      className="max-h-7 md:max-h-9 w-auto opacity-70 hover:opacity-100 transition-opacity object-contain"
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Analyse Détaillée - Style TestPage2 */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Badge de section - Style TestPage2 */}
            <div className="text-center mb-12">
              {/* Logo du client - Brandfetch API */}
              {siteUrl && <CompanyLogo siteUrl={siteUrl} className="mb-8" />}

              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analyse Détaillée
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Analyse Détaillée de Votre <span className="text-orange-600">Performance</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Diagnostic complet de votre présence digitale avec comparaison marché
              </p>
            </div>

            {/* Visualisation du Score - Style TestPage2 */}
            <motion.div 
              className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 mb-12 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex flex-col items-center">
                {/* Cercle de score animé - Centré */}
                <div className="relative w-64 h-64 mx-auto mb-8">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Cercle de fond */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      stroke="rgba(249,115,22,0.1)"
                      strokeWidth="8"
                      fill="none"
                    />
                    {/* Cercle de progression */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      stroke="url(#scoreGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${animatedScore * 2.83} 283`}
                      className="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#F97316" />
                        <stop offset="100%" stopColor="#EA580C" />
                      </linearGradient>
                    </defs>
                  </svg>
                  
                  {/* Contenu central */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-gray-900 mb-2">{animatedScore}%</div>
                      <div className="text-lg text-gray-600">Score Global</div>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold mt-2 ${
                        animatedScore >= 80 ? 'bg-green-100 text-green-800' :
                        animatedScore >= 60 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {animatedScore >= 80 ? <CheckCircle className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                        {animatedScore >= 80 ? 'Excellent' : animatedScore >= 60 ? 'Moyen' : 'Critique'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails par catégorie - Centré */}
                <div className="w-full max-w-2xl">
                  <div className="grid grid-cols-3 gap-4">
                    {score.categories.map((category, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-orange-600">{category.score}%</div>
                        <div className="text-sm text-gray-600">{category.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Zone de Friction - Problèmes Critiques avec Comparaison Marché */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Comparaison avec vos <span className="text-orange-600">concurrents</span>
                </h3>
                <p className="text-gray-600">Votre position sur le marché digital</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: Zap,
                    title: "Vitesse de Chargement",
                    yourScore: score.categories[0]?.score || 0,
                    marketAverage: 72,
                    topCompetitors: 89,
                    impact: "40% des visiteurs partent après 3s",
                    lostRevenue: Math.round(businessImpact.monthlyLoss * 0.4),
                    color: "red",
                    priority: "CRITIQUE"
                  },
                  {
                    icon: Target,
                    title: "Référencement SEO",
                    yourScore: score.categories[1]?.score || 0,
                    marketAverage: 65,
                    topCompetitors: 85,
                    impact: "76% du trafic Google manqué",
                    lostRevenue: Math.round(businessImpact.monthlyLoss * 0.35),
                    color: "orange",
                    priority: "URGENT"
                  },
                  {
                    icon: Shield,
                    title: "Expérience Utilisateur",
                    yourScore: score.categories[2]?.score || 0,
                    marketAverage: 68,
                    topCompetitors: 82,
                    impact: "Taux de conversion -23%",
                    lostRevenue: Math.round(businessImpact.monthlyLoss * 0.25),
                    color: "yellow",
                    priority: "IMPORTANT"
                  }
                ].map((issue, index) => (
                  <motion.div 
                    key={index}
                    className="bg-white rounded-3xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    {/* Header avec badge de priorité intégré */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className={`p-3 rounded-2xl mr-4 flex-shrink-0 ${
                          issue.color === 'red' ? 'bg-red-100 text-red-600' :
                          issue.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          <issue.icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 pr-2">{issue.title}</h3>
                          <p className="text-sm text-gray-600">{issue.impact}</p>
                        </div>
                      </div>
                      {/* Badge de priorité repositionné */}
                      <div className={`px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ml-2 ${
                        issue.priority === 'CRITIQUE' ? 'bg-red-100 text-red-700' :
                        issue.priority === 'URGENT' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {issue.priority}
                      </div>
                    </div>
                    
                    {/* Comparaison scores */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Votre score</span>
                        <div className="flex items-center gap-2">
                          <div className={`w-16 h-2 rounded-full ${
                            issue.yourScore >= 80 ? 'bg-green-200' :
                            issue.yourScore >= 60 ? 'bg-orange-200' : 'bg-red-200'
                          }`}>
                            <div 
                              className={`h-2 rounded-full transition-all duration-1000 ${
                                issue.yourScore >= 80 ? 'bg-green-500' :
                                issue.yourScore >= 60 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${issue.yourScore}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-bold text-gray-900">{issue.yourScore}%</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Moyenne marché</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full">
                            <div className="h-2 bg-gray-400 rounded-full" style={{ width: `${issue.marketAverage}%` }}></div>
                          </div>
                          <span className="text-sm text-gray-600">{issue.marketAverage}%</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Top concurrents</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-green-200 rounded-full">
                            <div className="h-2 bg-green-500 rounded-full" style={{ width: `${issue.topCompetitors}%` }}></div>
                          </div>
                          <span className="text-sm font-bold text-green-600">{issue.topCompetitors}%</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA de rappel - Fond gris pour alternance */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-center shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="max-w-2xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Ne laissez pas <span className="text-yellow-300">{animatedScore}%</span> de performance vous coûter des clients
                </h3>
                <p className="text-orange-100 text-lg mb-6">
                  Obtenez votre roadmap personnalisée avec ROI estimé pour chaque action
                </p>
                <div className="flex flex-col items-center gap-2">
                  <motion.button 
                    className="bg-white text-orange-600 font-bold text-xl px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-300 inline-flex items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleRequestAudit}
                  >
                    <FileText className="mr-3" size={24} />
                    Télécharger mon plan de croissance GRATUIT
                    <svg className="ml-3 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                  <span className="text-orange-200 text-sm font-semibold">(Valeur 790 CHF)</span>
                </div>
                
                {/* Indicateurs de confiance */}
                <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 mt-6 text-orange-100 text-sm">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    Livraison sous 24h
                  </span>
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    100% gratuit
                  </span>
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    +160 entreprises
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Plan d'Action - Fond blanc pour alternance */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            <motion.div 
              className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <div className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-4">
                    <Brain className="w-4 h-4 mr-2" />
                    Aperçu de votre potentiel
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    Ce que nous avons <span className="text-orange-600">détecté</span> pour vous
                  </h3>
                  <p className="text-gray-600">
                    Voici un aperçu des opportunités cachées que notre IA a identifiées
                  </p>
                  <div className="mt-4 inline-flex items-center px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    <span><span className="font-semibold">Analyse partielle</span> • Le rapport complet contient <strong>12+ leviers spécifiques</strong></span>
                  </div>
                </div>

                {/* Opportunités Détectées - Version Teaser */}
                <div className="space-y-4 mb-8">
                  {[
                    {
                      icon: Zap,
                      category: "Performance & Vitesse",
                      detected: "7 optimisations critiques identifiées",
                      preview: "Votre site perd 40% de visiteurs à cause du temps de chargement",
                      potential: "+15-25%",
                      locked: true,
                      color: "red"
                    },
                    {
                      icon: Target,
                      category: "Référencement SEO",
                      detected: "23 mots-clés à fort potentiel non exploités",
                      preview: "Vos concurrents captent 76% du trafic que vous pourriez avoir",
                      potential: "+35-50%",
                      locked: true,
                      color: "orange"
                    },
                    {
                      icon: TrendingUp,
                      category: "Conversion & Expérience",
                      detected: "5 points de friction majeurs dans le parcours client",
                      preview: "88% de vos visiteurs partent sans convertir (vs 65% moyenne marché)",
                      potential: "+20-35%",
                      locked: true,
                      color: "yellow"
                    }
                  ].map((opportunity, index) => (
                    <motion.div 
                      key={index}
                      className="relative bg-gradient-to-r from-white to-gray-50 rounded-2xl p-6 border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.15 }}
                    >
                      {/* Badge verrouillé */}
                      <div className="absolute top-4 right-4 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Détails dans l'audit
                      </div>

                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl flex-shrink-0 ${
                          opportunity.color === 'red' ? 'bg-red-100 text-red-600' :
                          opportunity.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          <opportunity.icon size={24} />
                        </div>
                        
                        <div className="flex-1 pr-24">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{opportunity.category}</h4>
                          <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded mb-3">
                            <p className="text-sm font-semibold text-orange-800">
                              🔍 {opportunity.detected}
                            </p>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{opportunity.preview}</p>
                          
                          {/* Potentiel flouté */}
                          <div className="relative">
                            <div className="absolute inset-0 backdrop-blur-sm bg-white/60 flex items-center justify-center rounded-lg">
                              <span className="text-xs font-bold text-gray-700 bg-white px-3 py-1 rounded-full shadow-sm">
                                🔒 Stratégie complète dans l'audit
                              </span>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 blur-sm">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-green-700">Potentiel de croissance</span>
                                <span className="text-xl font-bold text-green-600">{opportunity.potential}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Teaser du contenu complet */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-2xl p-6 mb-8">
                  <div className="text-center">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">
                      🎯 Ce n'est que la partie émergée de l'iceberg
                    </h4>
                    <p className="text-gray-700 mb-4">
                      L'audit complet contient <strong>12+ leviers spécifiques</strong> adaptés à votre secteur, avec :
                    </p>
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-orange-600">35+</div>
                        <div className="text-xs text-gray-600">Actions prioritaires</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-orange-600">ROI</div>
                        <div className="text-xs text-gray-600">Calculé par action</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-orange-600">12 sem.</div>
                        <div className="text-xs text-gray-600">Roadmap détaillée</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic">
                      + Analyse concurrentielle + Stratégie mots-clés + Plan de contenu + Optimisations techniques
                    </p>
                  </div>
                </div>
                
                {/* CTA pour débloquer le contenu complet */}
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-center">
                  <h4 className="text-xl font-bold text-white mb-3">
                    🔓 Débloquez votre stratégie complète
                  </h4>
                  <p className="text-gray-300 mb-6">
                    Recevez l'audit détaillé avec toutes les actions prioritaires, le ROI prévisionnel et la roadmap mois par mois
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <motion.a
                      href="https://tidycal.com/smartimpact/25-minutes-meeting"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 inline-flex items-center shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone className="mr-2" size={20} />
                      Appel Expert (25min)
                    </motion.a>
                    <motion.button
                      onClick={handleRequestAudit}
                      className="bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 inline-flex items-center border border-white/30"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FileText className="mr-2" size={20} />
                      Audit PDF GRATUIT (Valeur 790 CHF)
                    </motion.button>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span>Garantie de résultats ou remboursement intégral</span>
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Section CTA Final - Fond gris clair dédié */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-8 md:p-12">
                <div className="text-center max-w-3xl mx-auto">
                  {/* URL du site analysé */}
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-2">Analyse pour</p>
                    <div className="inline-block bg-orange-50 border border-orange-200 rounded-lg px-6 py-3">
                      <span className="font-mono text-lg font-bold text-orange-600">
                        {siteUrl || 'votre-site.com'}
                      </span>
                    </div>
                  </div>

                  {/* Témoignages rotatifs */}
                  <div className="mb-8">
                    <div className="flex justify-center items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                      <span className="text-yellow-400 font-bold ml-2">4.9/5</span>
                    </div>
                    
                    {/* Carousel de témoignages */}
                    <div className="relative h-24 overflow-hidden">
                      {testimonials.map((testimonial, index) => (
                        <motion.div
                          key={index}
                          className="absolute inset-0 flex flex-col items-center justify-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{
                            opacity: currentTestimonial === index ? 1 : 0,
                            y: currentTestimonial === index ? 0 : 20
                          }}
                          transition={{ duration: 0.5 }}
                        >
                          <p className="text-gray-700 text-lg italic mb-2 text-center px-4">
                            "{testimonial.text}"
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.author} - {testimonial.company}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Indicateurs de pagination */}
                    <div className="flex justify-center gap-2 mt-4">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentTestimonial(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            currentTestimonial === index 
                              ? 'bg-orange-500 w-6' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                          aria-label={`Témoignage ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                    Rejoignez les <span className="text-orange-600">400+ entreprises suisses</span> qui ont transformé leur digital
                  </h3>
                  
                  {/* CTA principal */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                    <motion.a 
                      href="https://tidycal.com/smartimpact/25-minutes-meeting"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 inline-flex items-center shadow-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone className="mr-2" size={20} />
                      Parler à un expert (25min)
                    </motion.a>
                  </div>
                  
                  {/* Réassurance minimaliste */}
                  <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
                    <span>100% gratuit</span>
                    <span>•</span>
                    <span>Livraison 24h</span>
                    <span>•</span>
                    <span>Garantie résultats</span>
                    <span>•</span>
                    <span>Swiss Made</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer - Fond blanc avec séparation claire */}
      <footer className="bg-white py-12 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <p className="text-xs text-gray-500">
            Cette analyse est basée sur des données publiques et respecte pleinement les réglementations 
            suisses et européennes sur la protection des données.
          </p>
        </div>
      </footer>

      {/* Sticky CTA Bar - Mobile & Desktop */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-gray-900 to-gray-800 border-t-2 border-orange-500 shadow-2xl z-50"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4 py-3">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Message */}
                <div className="text-center sm:text-left">
                  <p className="text-white font-bold text-sm sm:text-base">
                    🚀 Prêt à débloquer votre croissance ?
                  </p>
                  <p className="text-orange-300 text-xs">
                    Audit complet offert • Valeur 790 CHF
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex gap-3 items-center">
                  <motion.a
                    href="https://tidycal.com/smartimpact/25-minutes-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 py-3 rounded-lg transition-all duration-300 inline-flex items-center text-sm shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Appel Expert
                  </motion.a>

                  <motion.button
                    onClick={handleRequestAudit}
                    className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 inline-flex items-center text-sm border border-white/30"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Plan PDF
                  </motion.button>

                  <button
                    onClick={() => setShowStickyBar(false)}
                    className="text-gray-400 hover:text-white transition-colors p-2"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup de confirmation email */}
      {showEmailPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Audit SEO envoyé !
              </h3>

              <p className="text-gray-600 mb-6">
                Votre demande d'audit SEO personnalisé a été enregistrée avec succès !
                <br /><br />
                <strong>📧 Vous recevrez votre rapport détaillé sous 24h</strong> directement dans votre boîte de réception.
                <br /><br />
                N'oubliez pas de vérifier vos <strong>dossiers spam/indésirables</strong> si vous ne le trouvez pas.
              </p>

              <button
                onClick={() => setShowEmailPopup(false)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-300 hover:scale-105"
              >
                Parfait, merci !
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ScorePage2;
